import React from 'react';
import { Box, CircularProgress, useTheme } from '@mui/material';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  XAxisProps,
  YAxis,
  YAxisProps,
} from 'recharts';
import { ApolloError } from '@apollo/client';

import { Typography } from '@occc-app/design-system';
import { chartCartesianAxisColor, chartCartesianGridColor } from '@occc-app/themes/colors';
import { AudienceDailyCount } from '@occc/graphql';
import { DateTimeFormat, DurationUnit, formatDateTime, parseDate } from '@occc-app/lib/date';

import { AudienceSizeChartTooltip } from './AudienceSizeChartTooltip';

interface IChartAxesProps {
  XAxis: XAxisProps;
  YAxis: YAxisProps;
}

const axisProps = {
  tick: {
    fontSize: 12,
    fill: chartCartesianAxisColor,
  },
  stroke: chartCartesianAxisColor,
  tickLine: false,
  axisLine: false,
};

const tickFormatter = (date: any, interval: any) => {
  if (interval === 'hour') {
    return formatDateTime(parseDate(date), DateTimeFormat.Time12h) + ' UTC';
  } else {
    return formatDateTime(parseDate(date), DateTimeFormat.Date);
  }
};

const getChartAxesProps = (data: any): IChartAxesProps => {
  return {
    XAxis: {
      dataKey: 'date',
      padding: { left: 56, right: 0 },
      interval: 'preserveStartEnd',
      tickMargin: 10,
      tickFormatter: (tick) => tickFormatter(tick, data),
      ...axisProps,
    },
    YAxis: {
      mirror: true,
      ...axisProps,
      tick: {
        ...axisProps.tick,
        transform: 'translate(-8, -8)',
      },
    },
  };
};

const renderTooltip = (props: any) => {
  return <AudienceSizeChartTooltip {...props} />;
};

interface AudienceSizeChartProps {
  data?: AudienceDailyCount[];
  loading: boolean;
  error?: ApolloError;
  interval: DurationUnit;
}

// TODO: think about base Area chart with default props?
export const AudienceSizeChart: React.FC<AudienceSizeChartProps> = ({ data, loading, error }) => {
  const { palette } = useTheme();

  const chartAxesProps = React.useMemo(() => getChartAxesProps(data || []), [data]);

  if (error) {
    return <Typography color='error'>Something went wrong. Please try again.</Typography>;
  }

  if (loading && !data) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center'>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return <Typography color='error'>No data. Something went wrong. Please try again.</Typography>;
  }

  return (
    <Box position='relative' width='99%' height={200}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 16, right: 16, left: 16, bottom: 0 }}>
          <XAxis {...chartAxesProps.XAxis} />
          <YAxis {...chartAxesProps.YAxis} />
          <CartesianGrid stroke={chartCartesianGridColor} vertical={false} />
          <Tooltip content={renderTooltip} />
          <Area
            type='linear'
            dataKey='count'
            stroke={palette.primary.main}
            strokeWidth={2}
            fillOpacity={0.15}
            fill={palette.primary.main}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};
